/* Spotta site behaviour: header, scroll reveals, headline splits, statements, accordions, timetable. */
(() => {
  'use strict';
  window.__spottaReady = true;
  const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Headline word split (the words rise out of a mask)
  function split(el) {
    if (el.classList.contains('sp-split')) return;
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    if (!text) return;
    el.setAttribute('aria-label', text);
    el.textContent = '';
    text.split(' ').forEach((word, i) => {
      const w = document.createElement('span');
      w.className = 'sp-w';
      w.setAttribute('aria-hidden', 'true');
      const wi = document.createElement('span');
      wi.className = 'sp-wi';
      wi.style.setProperty('--i', i);
      wi.textContent = word;
      w.appendChild(wi);
      el.appendChild(w);
      el.appendChild(document.createTextNode(' '));
    });
    el.classList.add('sp-split');
  }

  // Statement: words light up as the paragraph scrolls through the viewport
  const statements = [];
  function prepStatement(el) {
    if (el.dataset.ready) return;
    const text = el.textContent.replace(/\s+/g, ' ').trim();
    el.setAttribute('aria-label', text);
    el.textContent = '';
    const words = text.split(' ').map((word) => {
      const s = document.createElement('span');
      s.className = 'sp-word';
      s.setAttribute('aria-hidden', 'true');
      s.textContent = word + ' ';
      el.appendChild(s);
      return s;
    });
    el.dataset.ready = '1';
    statements.push({ el, words, lit: -1 });
  }

  let io;
  function observe(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-split]').forEach(split);
    scope.querySelectorAll('[data-words]').forEach(prepStatement);
    if (!io) {
      io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    }
    scope.querySelectorAll('[data-reveal]').forEach((el) => {
      if (el.closest('[data-spotta-wall]') || el.classList.contains('is-in')) return;
      if (reduced) { el.classList.add('is-in'); return; }
      io.observe(el);
    });
    scope.querySelectorAll('[data-sp-nav]').forEach(initNav);
    scope.querySelectorAll('details[data-acc]').forEach(initAccordion);
    scope.querySelectorAll('[data-timetable]').forEach(initTimetable);
    onScroll();
  }

  // Header: frosted once you scroll, tucks away on the way down, returns on the way up
  const navs = [];
  function initNav(nav) {
    if (nav.dataset.ready) return;
    nav.dataset.ready = '1';
    navs.push(nav);
    const toggle = nav.querySelector('[data-menu-toggle]');
    const menu = nav.querySelector('[data-menu]');
    if (!toggle || !menu) return;
    const setOpen = (open) => {
      toggle.setAttribute('aria-expanded', String(open));
      nav.classList.toggle('is-open', open);
      document.documentElement.classList.toggle('sp-lock', open);
      if (open) { menu.hidden = false; requestAnimationFrame(() => menu.classList.add('is-open')); }
      else {
        menu.classList.remove('is-open');
        setTimeout(() => { if (!nav.classList.contains('is-open')) menu.hidden = true; }, 500);
      }
    };
    toggle.addEventListener('click', () => setOpen(toggle.getAttribute('aria-expanded') !== 'true'));
    menu.addEventListener('click', (e) => { if (e.target.closest('a')) setOpen(false); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && nav.classList.contains('is-open')) { setOpen(false); toggle.focus(); } });
  }

  // Accordion with a smooth height animation
  function initAccordion(d) {
    if (d.dataset.ready) return;
    d.dataset.ready = '1';
    const summary = d.querySelector('summary');
    const body = d.querySelector('[data-acc-body]');
    if (!summary || !body) return;
    summary.addEventListener('click', (e) => {
      if (reduced) return;
      e.preventDefault();
      if (d.open) {
        const h = body.offsetHeight;
        d.classList.remove('is-open');
        body.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 420, easing: 'cubic-bezier(.16,1,.3,1)' }).onfinish = () => { d.open = false; };
      } else {
        d.open = true;
        d.classList.add('is-open');
        const h = body.offsetHeight;
        body.animate([{ height: '0px', opacity: 0 }, { height: h + 'px', opacity: 1 }], { duration: 560, easing: 'cubic-bezier(.16,1,.3,1)' });
      }
    });
    if (d.open) d.classList.add('is-open');
  }

  // Timetable: highlight today, and on phones start the day strip at today
  function initTimetable(t) {
    if (t.dataset.ready) return;
    t.dataset.ready = '1';
    const today = String(new Date().getDay());
    const card = t.querySelector(`[data-day="${today}"]`);
    const strip = t.querySelector('[data-strip]');
    if (card) card.classList.add('is-today');
    if (!strip) return;
    const days = [...strip.querySelectorAll('.sp-day')];

    // Reveal every day together once the strip is in view. On phones most days sit off to the side,
    // and fading each one in as it is swiped into view makes iPhone Safari drop the swipe.
    if ('IntersectionObserver' in window && !reduced) {
      const ro = new IntersectionObserver((entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        days.forEach((d) => d.classList.add('is-in'));
        ro.disconnect();
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
      ro.observe(strip);
    }

    if (card && strip.scrollWidth > strip.clientWidth) strip.scrollLeft = card.offsetLeft - strip.offsetLeft - 20;

    // Previous / next buttons (shown on phones and tablets only)
    const prev = t.querySelector('[data-strip-prev]');
    const next = t.querySelector('[data-strip-next]');
    if (!prev || !next) return;
    const step = () => {
      const d = days[0];
      const gap = parseFloat(getComputedStyle(strip).columnGap) || 10;
      return d ? d.getBoundingClientRect().width + gap : strip.clientWidth * 0.8;
    };
    const sync = () => {
      const max = strip.scrollWidth - strip.clientWidth;
      prev.disabled = strip.scrollLeft <= 4;
      next.disabled = strip.scrollLeft >= max - 4;
    };
    const go = (dir) => strip.scrollBy({ left: dir * step(), behavior: reduced ? 'auto' : 'smooth' });
    prev.addEventListener('click', () => go(-1));
    next.addEventListener('click', () => go(1));
    strip.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    sync();
  }

  // One rAF-throttled scroll handler for everything scroll-linked
  let lastY = window.scrollY;
  let ticking = false;
  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      const y = window.scrollY;
      const vh = window.innerHeight;
      navs.forEach((nav) => {
        nav.classList.toggle('is-scrolled', y > 24);
        if (!nav.classList.contains('is-open')) nav.classList.toggle('is-tucked', y > 420 && y > lastY + 2);
        if (y < lastY - 2) nav.classList.remove('is-tucked');
      });
      document.querySelectorAll('[data-hero-fade]').forEach((el) => {
        const r = el.parentElement.getBoundingClientRect();
        const p = Math.min(1, Math.max(0, -r.top / (r.height * 0.7)));
        el.style.setProperty('--fade', p.toFixed(3));
      });
      statements.forEach((s) => {
        const r = s.el.getBoundingClientRect();
        const p = reduced ? 1 : Math.min(1, Math.max(0, (vh * 0.88 - r.top) / (r.height + vh * 0.42)));
        const lit = Math.round(p * s.words.length);
        if (lit !== s.lit) {
          s.words.forEach((w, i) => w.classList.toggle('is-lit', i < lit));
          s.lit = lit;
        }
      });
      lastY = y;
    });
  }
  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onScroll);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => observe());
  else observe();
  document.addEventListener('shopify:section:load', (e) => observe(e.target));
})();
